import {OrderUi} from "../../types/OrderListUi.ts";
import {RootState, useAppDispatch, useAppSelector} from "../../../../state/state.tsx";
import {useEffect, useState, useCallback} from "react";
import {SubscriptionDocWithId} from "../../types/SubscriptionType.ts";
import {FormControl, MenuItem, TextField, Button} from "@mui/material";
import OrdersListRenderer from "./Components/OrdersListRenderer.tsx";
import StatsForOrders from "./Components/StatsForOrders.tsx";
import {setFieldsForStatsForOrders} from "../../../../state/ArbitrarySlice/ArbitratySlice.ts";
import {useLocation} from "react-router-dom";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { openModal } from "../../../../state/ModalSlice/ModalSlice.ts";
import CreateFollowupModal from "../Inflow/Components/CreateFollowupModal.tsx";

interface OrdersProps {
    subscriptionId?: string;
    liveSubscriptionId?: string;
    standalone?: boolean;
}

export default function Orders({ subscriptionId, liveSubscriptionId, standalone = true }: OrdersProps) {
    const Dispatch = useAppDispatch();
    const orderState: OrderUi[] = useAppSelector((state: RootState) => state.OrderStatsSlice.data);
    const AllSubscriptions: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice.data);
    const location = useLocation();

    // States
    const [selectedCycle, setSelectedCycle] = useState<string>("All Cycles");
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>("0");
    const [paymentStatus, setPaymentStatus] = useState<'all' | 'Paid' | 'Pending'>('all');
    const [followupOrderData, setFollowupOrderData] = useState<any>(null);

    // Get subscriptionId from URL parameters
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const subId = params.get('subscriptionId');
        if (subId) {
            setSelectedSubscriptionId(subId);
        } else if (subscriptionId) {
            setSelectedSubscriptionId(subscriptionId);
        }
    }, [location.search, subscriptionId]);

    // Updating state for stats in orders page
    useEffect(() => {
        if(selectedSubscriptionId==="0"){
            Dispatch(
                setFieldsForStatsForOrders({
                    subscriptionId: "",
                    subscriptionName: "",
                    cycleName: selectedCycle
                })
            )
        }else{
            Dispatch(setFieldsForStatsForOrders({
                subscriptionId: selectedSubscriptionId,
                subscriptionName: "",
                cycleName: selectedCycle
            }))
        }
    }, [Dispatch, selectedSubscriptionId,selectedCycle]);

    //Utilities
    // Extract unique cycles only if it is defined
    const uniqueCycles = useCallback(() => Array.from(
        new Set(
            orderState
                .map(order => (order.cycleName !== undefined ? order.cycleName : null))
                .filter(cycleName => cycleName !== null)
        )
    ), [orderState]);
    const uniqueCyclesWithAll: string[] = ["All Cycles", ...uniqueCycles()];

    const uniqueSubscriptions = AllSubscriptions.map((item) => {
        return {id: item.id, name: item.Name}
    })


    useEffect(() => {
        if (uniqueCycles().length > 0) {
            setSelectedCycle(uniqueCycles()[0]);
        }
    }, [uniqueCycles]);
    

    const uniqueSubscriptionsWithAll = [{name: "All Subscriptions", id: "0"}, ...uniqueSubscriptions]

    // Filter orders based on props and filters
    const filteredOrders = orderState.filter((order) => {
        const cycleMatch = selectedCycle === "All Cycles" || order.cycleName === selectedCycle;
        const subscriptionMatch = standalone 
            ? (selectedSubscriptionId === "0" || order.subscriptionId === selectedSubscriptionId)
            : order.subscriptionId === subscriptionId;
        const liveSubMatch = !liveSubscriptionId || order.livesubscriptionId === liveSubscriptionId;

        const paymentMatch = paymentStatus === 'all' || order.paymentstatus === paymentStatus;

        return cycleMatch && subscriptionMatch && liveSubMatch && paymentMatch;
    });



    return (
        <div className={standalone ? "grid grid-cols-4" : ""}>
            <div className={standalone ? "orders-left col-span-3 p-4" : ""}>
                <div>
                    <FormControl fullWidth sx={{mb: 2}}>
                        <TextField
                            size={"small"}
                            id="SelectCycles"
                            select
                            fullWidth={true}
                            label="Select Cycle"
                            value={selectedCycle}
                            onChange={(e) => setSelectedCycle(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey', borderRadius: '10px'// Default border color
                                    }, '&:hover fieldset': {
                                        borderColor: 'grey', // Border color on hover
                                    }, '&.Mui-focused fieldset': {
                                        borderColor: 'skyblue', // Border color when focused
                                    }, '& .MuiInputBase-input': {
                                        fontSize: '14px', // Setting font size
                                    },
                                }, fontWeight: 400, fontSize: "1px",
                            }}
                        >
                            {uniqueCyclesWithAll.map((cycle, index) => (
                                <MenuItem key={index} value={cycle}>
                                    {cycle}
                                </MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    {/* Only show subscription selector in standalone mode */}
                    {standalone && (
                        <FormControl fullWidth sx={{mb: 2}}>
                            <TextField
                                size={"small"}
                                id="SelectSubscription"
                                select
                                fullWidth={true}
                                label="Select Subscription"
                                value={selectedSubscriptionId}
                                onChange={(e) => setSelectedSubscriptionId(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'grey', borderRadius: '10px'// Default border color
                                        }, '&:hover fieldset': {
                                            borderColor: 'grey', // Border color on hover
                                        }, '&.Mui-focused fieldset': {
                                            borderColor: 'skyblue', // Border color when focused
                                        }, '& .MuiInputBase-input': {
                                            fontSize: '14px', // Setting font size
                                        },
                                    }, fontWeight: 400, fontSize: "1px",
                                }}
                            >
                                {uniqueSubscriptionsWithAll.map((items, index) => (
                                    <MenuItem key={index} value={items.id}>
                                        {items.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FormControl>
                    )}

                    {/* Payment status filter */}
                    <div className="flex gap-2 mb-4 p-2">
                        <span
                            onClick={() => setPaymentStatus('all')}
                            className={`py-[3px] px-5 flex justify-center items-center  cursor-pointer  rounded-xl ${
                                paymentStatus === 'all' 
                                    ? 'bg-cc_brand text-white' 
                                    : 'bg-[#E8EDFB] text-cc_text-brand-tertiary'
                            }`}
                        >
                            All
                        </span>
                        <span
                            onClick={() => setPaymentStatus('Paid')}
                            className={`py-[4px] px-5 flex justify-center items-center  cursor-pointer  rounded-xl ${
                                paymentStatus === 'Paid' 
                                    ? 'bg-cc_brand text-white' 
                                    : 'bg-[#E8EDFB] text-cc_text-brand-tertiary'
                            }`}
                        >
                            Paid
                        </span>
                        <span
                            onClick={() => setPaymentStatus('Pending')}
                            className={`py-[3px] px-5 flex justify-center items-center  cursor-pointer  rounded-xl ${
                                paymentStatus === 'Pending' 
                                    ? 'bg-cc_brand text-white' 
                                    : 'bg-[#E8EDFB] text-cc_text-brand-tertiary'
                            }`}
                        >
                            Pending
                        </span>
                    </div>

                    {/* Send Bulk Reminders Button */}
                    <div className="flex justify-between items-center mb-4">
                        <div className="text-lg font-semibold">
                           <span className="text-lg font-base">Total Orders:</span> {filteredOrders.length} {paymentStatus !== 'all' ? `(${paymentStatus})` : ''}
                        </div>
                        <Button 
                            variant="contained"
                            startIcon={<NotificationsActiveIcon />}
                            onClick={() => {
                                Dispatch(openModal("BulkRemindersModalOpen"));
                              
                            }}
                            sx={{
                                backgroundColor: '#3862F8', // Using cc_brand color
                                borderRadius: '10px',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: '#2a4fd0',
                                },
                                fontWeight: '500',
                            }}
                        >
                            Send Bulk Reminders
                        </Button>
                    </div>

                    {/* Orders list */}
                    <div>
                        {filteredOrders?.map((item, index) => (
                            <div key={index} style={{ marginBottom: '16px' }}>
                                <OrdersListRenderer 
                                    orderDoc={item}
                                    onAddToCRM={(orderData) => {
                                        setFollowupOrderData(orderData);
                                        Dispatch(openModal("CreateFollowupModalOpen"));
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Only show stats in standalone mode */}
            {standalone && (
                <div className={"orders-right"}>
                    <StatsForOrders/>
                </div>
            )}
            
            {/* Follow-up Modal */}
            <CreateFollowupModal orderData={followupOrderData} />
        </div>
    );
}